import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/reset-password/verify`, { params: { token } });
        if (data.valid) {
          setEmail(data.email || "");
          setState("valid");
        } else {
          setState("invalid");
        }
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Please pick a password of at least 8 characters.");
      return;
    }
    if (pw !== pwConfirm) {
      toast.error("Passwords don't match. Please try again.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setState("done");
      toast.success("Your password has been reset.");
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      toast.error(detail);
      // If token became invalid mid-flight, force user to request a new one
      if (error.response?.status === 400) setState("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex flex-col">
      <header className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Button
          data-testid="reset-back-btn"
          onClick={() => navigate("/")}
          variant="ghost"
          className="text-primary hover:bg-primary/5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Home
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-8">
            {state === "checking" && (
              <div data-testid="reset-checking" className="text-center py-8">
                <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
                <p className="text-sm text-text-secondary font-body">Verifying your reset link…</p>
              </div>
            )}

            {state === "invalid" && (
              <div data-testid="reset-invalid" className="text-center py-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-3">
                  <XCircle className="w-7 h-7 text-red-500" />
                </div>
                <h1 className="text-xl font-heading font-bold text-text-primary">This link no longer works</h1>
                <p className="text-sm text-text-secondary font-body mt-2 leading-relaxed">
                  Reset links expire after 30 minutes or after being used once. Please head back to sign-in and request a new one.
                </p>
                <Button
                  data-testid="reset-invalid-back-btn"
                  onClick={() => navigate("/auth")}
                  className="mt-6 w-full min-h-[48px] bg-primary text-white hover:bg-primary-dark rounded-full font-bold font-body"
                >
                  Back to Sign In
                </Button>
              </div>
            )}

            {state === "valid" && (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                    <Lock className="w-6 h-6 text-primary" />
                  </div>
                  <h1 className="text-2xl font-heading font-bold text-text-primary">Choose a new password</h1>
                  {email && (
                    <p className="text-sm text-text-secondary font-body mt-1">
                      For <span className="font-semibold text-text-primary">{email}</span>
                    </p>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="font-body font-semibold">New password</Label>
                    <Input
                      data-testid="reset-new-password-input"
                      type="password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      required
                      minLength={8}
                      placeholder="At least 8 characters"
                      className="mt-1 h-12 font-body"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label className="font-body font-semibold">Confirm new password</Label>
                    <Input
                      data-testid="reset-confirm-password-input"
                      type="password"
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Re-enter your new password"
                      className="mt-1 h-12 font-body"
                    />
                  </div>
                  <Button
                    data-testid="reset-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full min-h-[48px] bg-primary text-white hover:bg-primary-dark rounded-full font-bold text-base shadow-md font-body"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset Password"}
                  </Button>
                </form>
              </>
            )}

            {state === "done" && (
              <div data-testid="reset-done" className="text-center py-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                  <CheckCircle2 className="w-7 h-7 text-primary" />
                </div>
                <h1 className="text-xl font-heading font-bold text-text-primary">Password reset ✓</h1>
                <p className="text-sm text-text-secondary font-body mt-2 leading-relaxed">
                  Your password has been updated and all previous sessions have been signed out. You can now sign in with your new password.
                </p>
                <Button
                  data-testid="reset-done-signin-btn"
                  onClick={() => navigate("/auth")}
                  className="mt-6 w-full min-h-[48px] bg-primary text-white hover:bg-primary-dark rounded-full font-bold font-body"
                >
                  Go to Sign In
                </Button>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-text-secondary font-body mt-6 px-4 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Protected by encrypted sessions and audit logging.
          </p>
        </div>
      </main>
    </div>
  );
}
